import config from '@common/config.js'
import { fail, success } from '@helpers/result-helper.js'
import router from '@router'
import msal from '@plugins/msal'
import toast from '@services/toasts/index.js'
import stringContainsAnySubstr from '@utils/string-contains-any-substr'
import $date from '@services/date/index.js'
import { isSuccess } from '@/helpers/http-status-helpers'
import divisions from '@/constants/admin/DivisionNameIdMap'
import { isCacheFresh } from '@/helpers/cache-helpers'
import { DurationUnits } from '@/constants/date/DurationUnits.js'
import { VuexResponse } from '@/helpers/vuex-action-builder'

export default {
  namespaced: true,
  state: {
    // MSAL User
    account: getSavedState('auth.account'),
    // User Profile from DB
    currentUser: getSavedState('auth.currentUser'),
    redirectFrom: getSavedState('auth.redirectFrom'),
    accessToken: '', // Bearer token
    lastTokenRefresh: null,
    loadingCount: 0,
  },

  mutations: {
    SET_CURRENT_USER(state, newValue) {
      state.currentUser = { ...newValue, ...{ lastUpdated: $date() } }
      saveState('auth.currentUser', state.currentUser)
    },
    SET_ACCOUNT(state, newValue) {
      state.account = newValue
      saveState('auth.account', newValue)
    },
    SET_ACCESS_TOKEN(state, token) {
      state.accessToken = token
      state.lastTokenRefresh = $date()
    },
    SET_USER_TO_UNAUTHENTICATED(state) {
      state.account = null
      saveState('auth.account', null)
      state.currentUser = null
      saveState('auth.currentUser', null)
      state.accessToken = null

      sessionStorage.clear()
    },
    SET_REDIRECT_FROM(state, url) {
      state.redirectFrom = url
      saveState('auth.redirectFrom', url)
    },
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    ADD_ROLES(state, roles) {
      state.currentUser.roles = roles
      saveState('auth.currentUser', state.currentUser)
    },
  },

  getters: {
    currentUserDateFormat: (state) =>
      state.dateFormat ? state.dateFormat : 'DD-MM-YYYY',
    currentUserFullName: (state) =>
      state.currentUser ? state.currentUser.name : '',
    currentUserEmail: (state) =>
      state.currentUser ? state.currentUser.email : '',
    currentUser: (state) => state.currentUser,
    currentUserId: (state) => state.currentUser?.id,
    currentAccount: (state) => state.account,
    redirectFrom: (state) => state.redirectFrom,
    accessToken: (state) => state.accessToken,
    lastTokenRefresh: (state) => state.lastTokenRefresh,
    isUserLoggedIn: (state) => !!state.account, // && msal.isAuthenticated(),
    isLoadingAuth: (state) => state.loadingCount > 0,
    auth: (state) => msal,
    currentUserTeams: (state) => state.currentUser.teams,
    currentUserDivision: (state) => state.currentUser.division,
    isCurrentUserDeveloper: (
      state,
      { currentUserDivision, currentUserEmail }
    ) =>
      currentUserDivision === divisions.IT_MELBOURNE ||
      currentUserEmail.includes('anzukdev'),
    isFinanceUser: (state) =>
      state?.currentUser?.roles
        ? state.currentUser.roles.some((item) => item.includes('Finance'))
        : false,
  },

  actions: {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    init({ dispatch }) {
      // dispatch('refreshToken')
    },

    // Clears the redirectFrom value from store and returns it to avoid old redirectFrom data
    comsumeRedirectFrom({ commit, getters }) {
      const redirect = getters.redirectFrom
      commit('SET_REDIRECT_FROM', null)
      return redirect
    },

    async handleRedirect({ commit, dispatch, getters, state }, response) {
      const redirect = await dispatch('comsumeRedirectFrom')

      if (response !== null) {
        await dispatch('getUserFromAuthProvider')
      }

      // In case multiple accounts exist, you can select
      const currentAccounts = msal.getAllAccounts()
      if (currentAccounts.length !== 0) {
        // TODO: Add choose account code here
        await dispatch('getUserFromAuthProvider')
      }

      // Using router.replace() here to stop the browser navigating to the
      // dashboard when the back button is pressed directly after a
      // successful login.
      if (redirect) router.replace(redirect)
    },

    // Logs in the current user.
    async logIn(
      { dispatch, getters, commit, rootGetters },
      redirectFrom = null
    ) {
      if (getters.isUserLoggedIn) return dispatch('refreshToken')

      // Maintain redirectFrom URL after redirect login
      commit('SET_REDIRECT_FROM', redirectFrom)

      const loginRequest = {
        scopes: ['openid'],
      }

      const errorHandler = (error) => {
        console.error(error)
        commit('SET_USER_TO_UNAUTHENTICATED')

        const errorCode = error.errorCode

        const noNotificationReq = ['user_cancelled']

        // Filter through errors that don't require a notifiction
        if (stringContainsAnySubstr(noNotificationReq, errorCode))
          return fail(error)

        toast.error('Oops.. Something went wrong')
        return fail(error)
      }

      if (rootGetters.isEmbedded) {
        msal
          .loginPopup(loginRequest)
          .then(async (response) => {
            return await dispatch('handleRedirect', response)
          })
          .catch(errorHandler)
      } else {
        msal.loginRedirect(loginRequest).catch(errorHandler)
      }
    },

    // Logs out the current user.
    async logOut({ commit, getters }) {
      commit('START_LOADING')
      return await msal
        .logout({})
        .catch((error) => {
          toast.error('Oops.. Something went wrong trying to sign you out')
          return fail(error)
        })
        .finally(() => {
          commit('FINISH_LOADING')
        })
    },

    // Retrieves user account from auth provider
    getUserFromAuthProvider({ commit, getters }) {
      if (!msal) return Promise.resolve(null)

      try {
        const myAccounts = msal.getAllAccounts()
        commit('SET_ACCOUNT', myAccounts[0])
      } catch {
        commit('SET_ACCOUNT', null)
      }
    },

    /**
     * Checks freshness of access token and will force refresh the token if token
     * is considered stale
     * @param {*} context Vuex context
     * @param {Boolean} forceRefresh Forces a token refresh
     * @returns Access token
     */
    async getAccessTokenOrRefresh({ dispatch, getters }, forceRefresh = false) {
      const isAccessTokenFresh = isCacheFresh({
        cacheDuration: 30,
        durationUnits: DurationUnits.MINUTE,
        lastUpdated: getters.lastTokenRefresh,
        forceRefresh,
      })

      if (!isAccessTokenFresh) {
        await dispatch('refreshToken', true)
      }

      return getters.accessToken
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    async refreshToken({ dispatch, commit, state, getters }) {
      if (!msal) return Promise.resolve(fail()) // Prevents trying to access auth object before it is initialised
      await dispatch('getUserFromAuthProvider')
      if (!state.account) return Promise.resolve(fail())

      commit('START_LOADING')

      const request = {
        scopes: [config.get('scopes.openId'), config.get('scopes.read')],
        account: getSavedState('auth.account'),
      }

      let response

      try {
        response = await msal.acquireTokenSilent(request)
        commit('SET_ACCESS_TOKEN', response.accessToken)
        return success()
      } catch (error) {
        console.warn('Silent token acquisition failed. Using interactive mode')
        return await msal
          .acquireTokenPopup(request)
          .then((response) => {
            commit('SET_ACCESS_TOKEN', response.accessToken)
            return success()
          })
          .catch(() => {
            toast.error('Failed to authenticate')
            return fail()
          })
      } finally {
        commit('FINISH_LOADING')
      }
    },

    async getCurrentUserProfile({ commit }) {
      commit('START_LOADING')
      try {
        const response = await this.$api.me.get()

        if (isSuccess(response.status)) {
          commit('SET_CURRENT_USER', response.data)
          return success()
        }
      } catch (ex) {
        toast.show("Couldn't load user profile")
        return fail(ex.response)
      } finally {
        commit('FINISH_LOADING')
      }
    },

    async getCurrentUserRoles({ commit, dispatch }) {
      return await new VuexResponse(commit, dispatch)
        .request(() => this.$api.me.getUserRoles())
        .withCommit('ADD_ROLES')
        .go()
    },
    clear({ commit }) {
      commit('SET_USER_TO_UNAUTHENTICATED')
    },
  },
}

// ===
// Private helpers
// ===

// eslint-disable-next-line no-unused-vars
function getSavedState(key) {
  const item = window.localStorage.getItem(key)
  return item && item !== 'undefined' ? JSON.parse(item) : ''
}

// eslint-disable-next-line no-unused-vars
function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

// function setDefaultAuthHeaders(state) {
//   axios.defaults.headers.common.Authorization = state.currentUser
//     ? state.currentUser.token
//     : ''
// }
